import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getCommitteeContext, setCommitteeContext } from '@/utils/committeeContext';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Award, FileText, Clock, CheckCircle, XCircle, AlertCircle, LogOut, 
  Plus, ChevronRight, Building2, 
  ChevronDown, ChevronUp, Lock, Unlock, Send, Loader2, Download, Timer
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CountdownTimer = ({ deadline }) => {
  const [timeLeft, setTimeLeft] = useState('');
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const end = new Date(deadline);
      const diff = end - now;
      
      if (diff <= 0) {
        return 'Closed';
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / 1000 / 60) % 60);
      
      if (days > 0) {
        return `${days}d ${hours}h left`;
      }
      if (hours > 0) {
        return `${hours}h ${minutes}m left`;
      }
      return `${minutes}m left`;
    };
    
    setTimeLeft(calculateTimeLeft());
    
    // Update every minute
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 60000);
    
    return () => clearInterval(timer);
  }, [deadline]);

  if (!timeLeft) return null;
  
  const isEndingSoon = timeLeft !== 'Closed' && !timeLeft.includes('d');
  
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${
      timeLeft === 'Closed' ? 'bg-red-100 text-red-700' : 
      isEndingSoon ? 'bg-amber-100 text-amber-700' : 
      'bg-slate-100 text-slate-700'
    }`}>
      <Timer className="w-3 h-3" />
      {timeLeft}
    </span>
  );
};

const statusConfig = {
  draft: { label: 'Draft', icon: FileText, class: 'badge-draft' },
  pending_sponsor: { label: 'Pending Endorsement', icon: AlertCircle, class: 'badge-pending' },
  submitted: { label: 'Submitted', icon: Clock, class: 'badge-submitted' },
  under_review: { label: 'Under Review', icon: Clock, class: 'badge-review' },
  awarded: { label: 'Awarded', icon: CheckCircle, class: 'badge-awarded' },
  rejected: { label: 'Not Selected', icon: XCircle, class: 'badge-rejected' }
};

const accessStatusConfig = {
  pending: { label: 'Pending Approval', icon: Clock, color: 'text-yellow-600 bg-yellow-50 border-yellow-200' },
  approved: { label: 'Approved', icon: CheckCircle, color: 'text-green-600 bg-green-50 border-green-200' },
  denied: { label: 'Denied', icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200' }
};

const ApplicantDashboard = () => {
  const { user, logout, stopImpersonation, loading: authLoading, token } = useAuth();
  const { committeeSlug } = useParams();
  const navigate = useNavigate();
  const [committeeContextState, setCommitteeContextState] = useState(committeeSlug ? getCommitteeContext() : null);
  const [applications, setApplications] = useState([]);
  const [scholarships, setScholarships] = useState([]);
  const [committees, setCommittees] = useState([]);
  const [accessRequests, setAccessRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('applications');
  const [expandedCommittees, setExpandedCommittees] = useState({});
  const [downloadingPdf, setDownloadingPdf] = useState(null);
  
  useEffect(() => {
    const resolveCommitteeContext = async () => {
      if (!committeeSlug) {
        setCommitteeContextState(null);
        return;
      }
      try {
        const response = await axios.get(`${API}/public/committees/${committeeSlug}`);
        const committee = response.data?.committee;
        if (committee) {
          setCommitteeContext(committee);
          setCommitteeContextState(committee);
          setCommittees([committee]);
          setExpandedCommittees({ [committee.id]: true });
        }
      } catch (error) {
        console.error('Failed to load committee context:', error);
      }
    };

    resolveCommitteeContext();
  }, [committeeSlug]);
  
  // Access request dialog
  const [accessDialog, setAccessDialog] = useState({ open: false, scholarship: null });
  const [eligibilityAnswers, setEligibilityAnswers] = useState({});
  const [submittingRequest, setSubmittingRequest] = useState(false);

  // Download application as PDF
  const downloadApplicationPdf = async (applicationId, scholarshipName) => {
    setDownloadingPdf(applicationId);
    try {
      const authToken = localStorage.getItem('token');
      const response = await fetch(`${API}/applications/${applicationId}/pdf`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      });

      if (!response.ok) {
        let errorMessage = 'Failed to download PDF';
        try {
          const errorData = await response.json();
          errorMessage = errorData?.detail || errorMessage;
        } catch (err) {
          if (response.status === 503) {
            errorMessage = 'PDF generation is not available at this time.';
          }
        }
        toast.error(errorMessage);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${scholarshipName.replace(/[^a-zA-Z0-9]/g, '_')}_Application.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleExitImpersonation = async () => {
    try {
      const impersonator = await stopImpersonation();
      if (impersonator?.role === 'admin') {
        window.location.href = '/admin';
      } else {
        window.location.href = '/my-committees';
      }
    } catch (error) {
      toast.error('Failed to exit impersonation');
    }
  };

  useEffect(() => {
    // Only fetch data after auth is confirmed ready and user is authenticated
    if (!authLoading && user && token) {
      fetchData();
    }
  }, [authLoading, user, token, committeeContextState?.id]);

  const fetchData = async () => {
    try {
      // Get token directly from localStorage for reliability
      const authToken = localStorage.getItem('token');
      const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
      const committeeId = committeeContextState?.id;

      const applicationsUrl = committeeId ? `${API}/applications?committee_id=${committeeId}` : `${API}/applications`;
      const scholarshipsUrl = committeeId
        ? `${API}/scholarships?active_only=true&committee_id=${committeeId}`
        : `${API}/scholarships?active_only=true`;
      const accessRequestsUrl = committeeId
        ? `${API}/my-access-requests?committee_id=${committeeId}`
        : `${API}/my-access-requests`;
      const committeesPromise = committeeId
        ? Promise.resolve({ data: [committeeContextState] })
        : axios.get(`${API}/committees`, { headers }).catch((err) => {
            console.error('Failed to fetch committees:', err.response?.status, err.response?.data);
            return { data: [] };
          });

      const [appsRes, scholarshipsRes, committeesRes, accessRes] = await Promise.all([
        axios.get(applicationsUrl, { headers }),
        axios.get(scholarshipsUrl),
        committeesPromise,
        axios.get(accessRequestsUrl, { headers }).catch(() => ({ data: [] }))
      ]);
      setApplications(appsRes.data);
      setScholarships(scholarshipsRes.data);
      setCommittees(committeesRes.data);
      setAccessRequests(accessRes.data);
      
      // Auto-expand first committee
      if (committeesRes.data.length > 0) {
        setExpandedCommittees({ [committeesRes.data[0].id]: true });
      }
    } catch (error) {
      console.error('Failed to load applicant data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const toggleCommittee = (committeeId) => {
    setExpandedCommittees(prev => ({
      ...prev,
      [committeeId]: !prev[committeeId]
    }));
  };

  // Helpers
  const parseDeadlineForComparison = (deadlineStr) => {
    if (!deadlineStr) return null;
    if (deadlineStr.length === 10) {
      const [year, month, day] = deadlineStr.split('-').map(Number);
      return new Date(year, month - 1, day, 23, 59, 59, 999);
    }
    const parsed = new Date(deadlineStr);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getAccessStatus = (scholarshipId) => {
    const request = accessRequests.find(r => r.scholarship_id === scholarshipId);
    return request?.status || null;
  };

  const getApplicationByScholarshipId = (scholarshipId) => {
    return applications.find(app => app.scholarship_id === scholarshipId) || null;
  };

  const hasApplication = (scholarshipId) => {
    return !!getApplicationByScholarshipId(scholarshipId);
  };

  const openAccessRequestDialog = (scholarship) => {
    setEligibilityAnswers({});
    setAccessDialog({ open: true, scholarship });
  };

  const submitAccessRequest = async () => {
    if (!accessDialog.scholarship) return;
    
    setSubmittingRequest(true);
    try {
      await axios.post(`${API}/scholarships/${accessDialog.scholarship.id}/request-access`, {
        scholarship_id: accessDialog.scholarship.id,
        eligibility_answers: eligibilityAnswers
      });
      
      toast.success('Access request submitted! You will be notified once reviewed.');
      setAccessDialog({ open: false, scholarship: null });
      fetchData(); // Refresh to show new request
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit access request');
    } finally {
      setSubmittingRequest(false);
    }
  };

  // Group scholarships by committee
  const getScholarshipsByCommittee = () => {
    const grouped = {};
    
    // Get scholarships with committees
    scholarships.forEach(scholarship => {
      const committeeId = scholarship.committee_id || 'unassigned';
      if (!grouped[committeeId]) {
        grouped[committeeId] = [];
      }
      grouped[committeeId].push(scholarship);
    });
    
    return grouped;
  };

  const basePath = committeeSlug ? `/${committeeSlug}` : '';

  const scholarshipsByCommittee = getScholarshipsByCommittee();

  // Get approved scholarships (can apply)
  const approvedScholarshipIds = accessRequests
    .filter(r => r.status === 'approved')
    .map(r => r.scholarship_id);

  // Get scholarships that don't require approval (no committee) or are approved
  const canApplyScholarships = scholarships.filter(s => 
    !s.committee_id || approvedScholarshipIds.includes(s.id)
  );

  // Available to apply (approved and not already applied)
  const availableToApply = canApplyScholarships.filter(
    s => !applications.some(app => app.scholarship_id === s.id)
  );

  // Show loading while auth is being verified or data is being fetched
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-greek-blue border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {user?.is_impersonating && (
        <div className="bg-blue-600 text-white py-2 px-6">
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              <span className="text-sm">Impersonation Mode: viewing as {user?.first_name} {user?.last_name}</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleExitImpersonation}
              className="border-white text-white hover:bg-white/10"
              data-testid="exit-impersonation-btn"
            >
              Exit Impersonation
            </Button>
          </div>
        </div>
      )}
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/1k2v2h81_AHEP_CA_LOGO.jfif" 
                alt="AHEPA Logo" 
                className="w-10 h-10 object-contain rounded-full"
              />
              <img 
                src="https://customer-assets.emergentagent.com/job_scholarship-hub-24/artifacts/gl3qcnxw_DOP_LOGO_LARGE.jfif" 
                alt="DOP Logo" 
                className="w-10 h-10 object-contain rounded-full"
              />
            </div>
            <div>
              <h1 className="font-heading text-lg font-bold text-greek-blue">AHEPA Family</h1>
              <p className="text-xs text-slate-500">Applicant Dashboard</p>
              {committeeContextState?.name && (
                <p className="text-xs text-blue-600 mt-1" data-testid="committee-scope-label">
                  Viewing {committeeContextState.name} scholarships
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="font-semibold text-slate-900">{user?.first_name} {user?.last_name}</p>
              <p className="text-sm text-slate-500">{user?.email}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="rounded-sm border-slate-300"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="font-heading text-3xl font-bold text-slate-900 mb-2">
            Welcome, {user?.first_name}!
          </h2>
          <p className="text-slate-600">Browse scholarships and manage your applications below.</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 bg-white border border-slate-200 p-1 rounded-sm">
            <TabsTrigger 
              value="applications" 
              className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white"
            >
              <FileText className="w-4 h-4 mr-2" />
              My Applications
              {applications.length > 0 && (
                <span className="ml-2 bg-greek-gold text-white text-xs px-2 py-0.5 rounded-full">
                  {applications.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger 
              value="browse" 
              className="rounded-sm data-[state=active]:bg-greek-blue data-[state=active]:text-white"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Browse Scholarships
            </TabsTrigger>
          </TabsList>

          {/* My Applications Tab */}
          <TabsContent value="applications">
            {/* Access Requests Status */}
            {accessRequests.length > 0 && (
              <div className="mb-8">
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Access Requests</h3>
                <div className="grid gap-3">
                  {accessRequests.map((request) => {
                    const statusInfo = accessStatusConfig[request.status];
                    const StatusIcon = statusInfo.icon;
                    return (
                      <div
                        key={request.id}
                        className={`bg-white rounded-sm border p-4 ${statusInfo.color}`}
                        data-testid={`access-request-${request.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <StatusIcon className="w-5 h-5" />
                            <div>
                              <p className="font-medium">{request.scholarship_name}</p>
                              <p className="text-sm opacity-75">
                                Requested: {new Date(request.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{statusInfo.label}</span>
                            {request.status === 'approved' && !hasApplication(request.scholarship_id) && (
                              <Link to={`${basePath}/application/${request.scholarship_id}`}>
                                <Button size="sm" className="bg-greek-blue hover:bg-blue-700">
                                  Start Application
                                </Button>
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Applications */}
            <div className="mb-8">
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">My Applications</h3>
              
              {applications.length === 0 ? (
                <div className="bg-white rounded-sm border border-slate-200 p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h4 className="font-heading text-xl font-semibold text-slate-700 mb-2">No Applications Yet</h4>
                  <p className="text-slate-500 mb-6">Browse scholarships and request access to start applying!</p>
                  <Button 
                    onClick={() => setActiveTab('browse')}
                    className="bg-greek-blue hover:bg-blue-700"
                  >
                    <Building2 className="w-4 h-4 mr-2" />
                    Browse Scholarships
                  </Button>
                </div>
              ) : (
                <div className="grid gap-4">
                  {applications.map((app) => {
                    const status = statusConfig[app.status] || statusConfig.draft;
                    const StatusIcon = status.icon;
                    return (
                      <div
                        key={app.id}
                        className="bg-white rounded-sm border border-slate-200 p-6 hover:shadow-md transition-shadow"
                        data-testid={`application-card-${app.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="font-heading text-lg font-semibold text-slate-900 mb-1">
                              {app.scholarship_name}
                            </h4>
                            <p className="text-sm text-slate-500">
                              Started: {new Date(app.created_at).toLocaleDateString()}
                              {app.last_saved_at && (
                                <span className="ml-2">
                                  • Last saved: {new Date(app.last_saved_at).toLocaleDateString()}
                                </span>
                              )}
                            </p>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-sm font-medium ${status.class}`}>
                              <StatusIcon className="w-4 h-4" />
                              {status.label}
                            </span>
                            {app.status === 'draft' || app.status === 'pending_sponsor' ? (
                              <Link to={`${basePath}/application/${app.scholarship_id}/${app.id}`}>
                                <Button className="bg-greek-blue hover:bg-greek-blue-dark text-white rounded-sm" data-testid={`continue-app-btn-${app.id}`}>
                                  Continue
                                  <ChevronRight className="w-4 h-4 ml-1" />
                                </Button>
                              </Link>
                            ) : (
                              <div className="flex items-center gap-2">
                                {app.status === 'submitted' && (
                                  <Button
                                    variant="outline"
                                    className="rounded-sm border-blue-300 text-blue-600 hover:bg-blue-50"
                                    onClick={() => downloadApplicationPdf(app.id, app.scholarship_name)}
                                    disabled={downloadingPdf === app.id}
                                    data-testid={`download-pdf-btn-${app.id}`}
                                  >
                                    <Download className="w-4 h-4 mr-1" />
                                    {downloadingPdf === app.id ? 'Downloading...' : 'PDF'}
                                  </Button>
                                )}
                                <Link to={`${basePath}/application/${app.scholarship_id}/${app.id}`}>
                                  <Button variant="outline" className="rounded-sm border-slate-300" data-testid={`view-app-btn-${app.id}`}>
                                    View
                                    <ChevronRight className="w-4 h-4 ml-1" />
                                  </Button>
                                </Link>
                              </div>
                            )}
                          </div>
                        </div>
                        {app.endorsement_status === 'completed' && (
                          <div className="mt-4 pt-4 border-t border-slate-100">
                            <p className="text-sm text-emerald-600 flex items-center gap-2">
                              <CheckCircle className="w-4 h-4" />
                              Chapter endorsement received
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Quick Apply Section for approved scholarships */}
            {availableToApply.length > 0 && (
              <div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-4">Ready to Apply</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availableToApply.map((scholarship) => (
                    <div
                      key={scholarship.id}
                      className="bg-white rounded-sm border border-slate-200 p-5 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h4 className="font-semibold text-slate-900 line-clamp-2">{scholarship.name}</h4>
                        <span className="font-bold text-greek-gold whitespace-nowrap ml-2">
                          ${scholarship.amount?.toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500 mb-3 flex items-center justify-between">
                        <span>Deadline: {new Date(scholarship.deadline).toLocaleDateString()}</span>
                        <CountdownTimer deadline={scholarship.deadline} />
                      </p>
                      <Link to={`${basePath}/application/${scholarship.id}`}>
                        <Button className="w-full bg-greek-blue hover:bg-blue-700">
                          <Plus className="w-4 h-4 mr-2" />
                          Start Application
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Browse Scholarships Tab */}
          <TabsContent value="browse">
            <div className="space-y-6">
              {/* Scholarships by Committee */}
              {committees.length > 0 ? (
                committees.map((committee) => {
                  const committeeScholarships = scholarshipsByCommittee[committee.id] || [];
                  const isExpanded = expandedCommittees[committee.id];
                  
                  if (committeeScholarships.length === 0) return null;
                  
                  return (
                    <div key={committee.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                      {/* Committee Header */}
                      <button
                        onClick={() => toggleCommittee(committee.id)}
                        className="w-full p-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                        data-testid={`committee-toggle-${committee.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-greek-blue/10 rounded-lg flex items-center justify-center">
                            <Building2 className="w-6 h-6 text-greek-blue" />
                          </div>
                          <div className="text-left">
                            <h3 className="font-semibold text-slate-900">{committee.name}</h3>
                            <p className="text-sm text-slate-500">
                              {committeeScholarships.length} scholarship{committeeScholarships.length !== 1 ? 's' : ''} available
                            </p>
                          </div>
                        </div>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400" />
                        )}
                      </button>
                      
                      {/* Scholarships List */}
                      {isExpanded && (
                        <div className="border-t border-slate-200 divide-y divide-slate-100">
                          {committeeScholarships.map((scholarship) => {
                            const accessStatus = getAccessStatus(scholarship.id);
                            const existingApp = getApplicationByScholarshipId(scholarship.id);
                            const hasApp = !!existingApp;
                            const deadline = parseDeadlineForComparison(scholarship.deadline);
                            const isDeadlinePassed = deadline ? deadline < new Date() : false;
                            
                            return (
                              <div key={scholarship.id} className="p-4 hover:bg-slate-50">
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="font-medium text-slate-900">{scholarship.name}</h4>
                                      {isDeadlinePassed && (
                                        <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">
                                          Closed
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                                      {scholarship.description}
                                    </p>
                                    <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                                      <span className="font-medium text-greek-gold">
                                        ${scholarship.amount?.toLocaleString()}
                                      </span>
                                      <span>Deadline: {new Date(scholarship.deadline).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                                      <CountdownTimer deadline={scholarship.deadline} />
                                    </div>
                                  </div>
                                  
                                  <div className="ml-4">
                                    {hasApp ? (
                                      <Link to={`${basePath}/application/${scholarship.id}/${existingApp.id}`}>
                                        <Button variant="outline" size="sm" data-testid={`view-existing-app-${existingApp.id}`}>
                                          {['draft', 'pending_sponsor'].includes(existingApp.status)
                                            ? 'Continue Application'
                                            : 'View Application'}
                                        </Button>
                                      </Link>
                                    ) : isDeadlinePassed ? (
                                      <Button variant="outline" size="sm" disabled>
                                        <Lock className="w-4 h-4 mr-1" />
                                        Closed
                                      </Button>
                                    ) : accessStatus === 'approved' ? (
                                      <Link to={`${basePath}/application/${scholarship.id}`}>
                                        <Button size="sm" className="bg-greek-blue hover:bg-blue-700">
                                          <Unlock className="w-4 h-4 mr-1" />
                                          Apply Now
                                        </Button>
                                      </Link>
                                    ) : accessStatus === 'pending' ? (
                                      <Button variant="outline" size="sm" disabled>
                                        <Clock className="w-4 h-4 mr-1" />
                                        Pending Approval
                                      </Button>
                                    ) : accessStatus === 'denied' ? (
                                      <Button variant="outline" size="sm" disabled className="text-red-600">
                                        <XCircle className="w-4 h-4 mr-1" />
                                        Access Denied
                                      </Button>
                                    ) : (
                                      <Button
                                        size="sm"
                                        onClick={() => openAccessRequestDialog(scholarship)}
                                        className="bg-greek-gold hover:bg-yellow-600 text-white"
                                        data-testid={`request-access-${scholarship.id}`}
                                      >
                                        <Send className="w-4 h-4 mr-1" />
                                        Request Access
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="bg-white rounded-lg border border-slate-200 p-8 text-center">
                  <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600">No committees available yet.</p>
                </div>
              )}
              
              {/* Scholarships without committee (direct apply) */}
              {scholarshipsByCommittee['unassigned']?.length > 0 && (
                <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                  <div className="p-4 border-b border-slate-200 bg-slate-50">
                    <h3 className="font-semibold text-slate-900">General Scholarships</h3>
                    <p className="text-sm text-slate-500">Apply directly without committee approval</p>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {scholarshipsByCommittee['unassigned'].map((scholarship) => {
                      const existingApp = getApplicationByScholarshipId(scholarship.id);
                      const hasApp = !!existingApp;
                      const deadline = parseDeadlineForComparison(scholarship.deadline);
                      const isDeadlinePassed = deadline ? deadline < new Date() : false;
                      
                      return (
                        <div key={scholarship.id} className="p-4 hover:bg-slate-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-slate-900 mb-1">{scholarship.name}</h4>
                              <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                                {scholarship.description}
                              </p>
                              <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                                <span className="font-medium text-greek-gold">
                                  ${scholarship.amount?.toLocaleString()}
                                </span>
                                <span>Deadline: {new Date(scholarship.deadline).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}</span>
                                <CountdownTimer deadline={scholarship.deadline} />
                              </div>
                            </div>
                            <div className="ml-4">
                              {hasApp ? (
                                <Link to={`${basePath}/application/${scholarship.id}/${existingApp.id}`}>
                                  <Button variant="outline" size="sm" data-testid={`view-existing-app-${existingApp.id}`}>
                                    {['draft', 'pending_sponsor'].includes(existingApp.status)
                                      ? 'Continue Application'
                                      : 'View Application'}
                                  </Button>
                                </Link>
                              ) : isDeadlinePassed ? (
                                <Button variant="outline" size="sm" disabled>Closed</Button>
                              ) : (
                                <Link to={`${basePath}/application/${scholarship.id}`}>
                                  <Button size="sm" className="bg-greek-blue hover:bg-blue-700">
                                    <Plus className="w-4 h-4 mr-1" />
                                    Apply Now
                                  </Button>
                                </Link>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Access Request Dialog */}
      <Dialog open={accessDialog.open} onOpenChange={(open) => setAccessDialog({ open, scholarship: null })}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Request Scholarship Access</DialogTitle>
            <DialogDescription>
              Submit your request to apply for this scholarship. The committee chair will review your eligibility.
            </DialogDescription>
          </DialogHeader>
          
          {accessDialog.scholarship && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-semibold text-slate-900">{accessDialog.scholarship.name}</h4>
                <p className="text-sm text-slate-600 mt-1">{accessDialog.scholarship.description}</p>
                <div className="flex items-center gap-4 mt-2 text-sm">
                  <span className="font-medium text-greek-gold">
                    ${accessDialog.scholarship.amount?.toLocaleString()}
                  </span>
                  <span className="text-slate-500 flex items-center gap-2">
                    Deadline: {new Date(accessDialog.scholarship.deadline).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                    <CountdownTimer deadline={accessDialog.scholarship.deadline} />
                  </span>
                </div>
              </div>
              
              {/* Eligibility Section */}
              <div className="border border-slate-200 rounded-lg p-4">
                <h5 className="font-medium text-slate-900 mb-3">Eligibility Confirmation</h5>
                <p className="text-sm text-slate-600 mb-4">
                  {accessDialog.scholarship.eligibility || 'Must be a student of Greek descent attending an accredited institution.'}
                </p>
                
                <div className="space-y-3">
                  <div>
                    <Label className="text-sm">Do you meet the eligibility requirements above?</Label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="meets_eligibility"
                          value="yes"
                          checked={eligibilityAnswers.meets_eligibility === 'yes'}
                          onChange={(e) => setEligibilityAnswers({ ...eligibilityAnswers, meets_eligibility: e.target.value })}
                          className="text-greek-blue"
                        />
                        <span className="text-sm">Yes, I meet all requirements</span>
                      </label>
                    </div>
                  </div>
                  
                  <div>
                    <Label className="text-sm">Brief statement of your eligibility (optional)</Label>
                    <Textarea
                      value={eligibilityAnswers.statement || ''}
                      onChange={(e) => setEligibilityAnswers({ ...eligibilityAnswers, statement: e.target.value })}
                      placeholder="Briefly explain how you meet the eligibility criteria..."
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAccessDialog({ open: false, scholarship: null })}>
              Cancel
            </Button>
            <Button
              onClick={submitAccessRequest}
              className="bg-greek-blue hover:bg-blue-700"
              disabled={submittingRequest || eligibilityAnswers.meets_eligibility !== 'yes'}
            >
              {submittingRequest ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Request
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApplicantDashboard;
